interface AvatarProps {
  name: string;
  photoUrl?: string | null;
  size?: "sm" | "md" | "lg";
}

const SIZE_CLASSES = {
  sm: "w-7 h-7 text-xs",
  md: "w-8 h-8 text-sm",
  lg: "w-10 h-10 text-base",
};

/** First letter of the first name + first letter of the last name (e.g.
 *  "John Smith" -> "JS"), not a blind slice of the raw string — "John
 *  Smith".slice(0, 2) gives "Jo", which isn't what anyone means by
 *  initials. A single-word name just shows that one letter. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.charAt(0).toUpperCase();
  return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
}

/** No photo storage exists anywhere in this codebase yet, so photoUrl
 *  is always undefined in practice today — this always renders the
 *  initials fallback. Kept as a real prop, not hardcoded to always-
 *  initials, so a real photo (an upload feature, or Google's own
 *  profile picture for Google-authenticated users) can be wired in
 *  later without touching every call site. */
export function Avatar({ name, photoUrl, size = "md" }: AvatarProps) {
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name}
        className={`rounded-full object-cover flex-shrink-0 ${SIZE_CLASSES[size]}`}
      />
    );
  }
  return (
    <div
      className={`rounded-full bg-indigo-500 flex items-center justify-center text-white font-semibold flex-shrink-0 ${SIZE_CLASSES[size]}`}
    >
      {initials(name)}
    </div>
  );
}
